import React, { Component } from 'react'
import { reaction, runInAction } from 'mobx'
import { observer, inject } from 'mobx-react'
import {
  Alert,
  Container,
  Row,
  Col
} from 'reactstrap'
import { ToastContainer, toast } from 'react-toastify'

/* components */
import Header from '../../components/Header'
import {
  SignUpModal,
  PremiumModal,
  FreeSlotModal,
  DeleteAccountModal,
  ReplacedModal,
  AudioMessageModal
} from '../../components/Modal'
import AudioElement from '../../components/Audio'
import NativeAudio from '../../components/NativeAudio'
import { hasNativeAudio, nativeAudio, appLog, isApp } from '../../helpers/nativeApp'
import Upcoming from '../../components/Upcoming'
import Channels from '../../components/Channels'
import EmailVerification from '../../components/EmailVerification'
import StudioPanel from '../../components/StudioPanel'
import SongProgress from '../../components/SongProgress'
import NowPlayingBar from '../../components/NowPlayingBar'
import StationHero from '../../components/StationHero'
import KeepScreenOn from '../../components/KeepScreenOn'
import Zones from '../../components/Zones'
import { MobileAndTablet } from '../../components/Responsive'
import { displayArtist, isAdvert } from '../../helpers/trackLabel'
import * as mediaSession from '../../helpers/mediaSession'
import {
  Close
} from '../../components/Icons'

/* css */
import 'react-toastify/dist/ReactToastify.css'
import './Player.css'

/* other */
import { isIOS } from '../../helpers/mobile-detect'
import * as freeAllowance from '../../helpers/freeAllowance'
import Analytics from '../../Analytics'
import config from '../../config'
// Lock screen, notification and car display picture while the phone app plays
const NOW_PLAYING_ART = '/assets/images/brand/gymradio-nowplaying.png'
const analytics = new Analytics()

class Player extends Component {
  audio = {}
  shouldRefreshPlaylist = true
  shouldRefreshAudioMessages = true
  playlistDisposer
  volumeDisposer
  audioMessageDisposer

  state = {
    playing: false,
    reportedSongs: {}, // songs reported as explicit since the player opened
    // Is necessary to load player after the first fetch requests is done
    // Otherwise it will fail on cors error
    // Explanation: https://serverfault.com/questions/856904/chrome-s3-cloudfront-no-access-control-allow-origin-header-on-initial-xhr-req
    loadPlayer: false,
    volume: 50,
    showSignUpModal: false,
    showPremiumModal: false,
    showFreeSlotModal: false,
    showDeleteAccountModal: false,
    deleteAccountBusy: false,
    deleteAccountError: '',
    deleteAccountDone: false,
    freeRemainingMs: null,
    songPosition: 0,
    songDuration: 0
  }

  async componentDidMount () {
    // Lock-screen / notification controls on phones and tablets
    mediaSession.setHandlers({
      onPlay: () => { if (!this.props.playerStore.playing) this.playPause(this.props.stationStore.selectedStation) },
      onPause: () => { if (this.props.playerStore.playing) this.pause() },
      onNext: () => { this.skip(this.props.stationStore.selectedStation) }
    })
    this.allowanceTimer = setInterval(() => this.tickFreeAllowance(), 1000)

    // Phone app reopened while the music is still playing: show it as playing
    if (hasNativeAudio()) {
      nativeAudio().getPosition().then((r) => {
        if (r && r.playing) {
          this.props.playerStore.playing = true
          this.setState({ loadPlayer: true })
        }
      }).catch(() => {})
    }
    this.progressTimer = setInterval(() => this.updateSongProgress(), 500)

    // Newest login wins: check every 90 seconds whether another device has
    // signed in to this account; if so, stop the music here.
    this.activeLoginTimer = setInterval(() => this.props.userStore.checkActiveLogin(), 90 * 1000)
    // Signing out stops the music (in the app the engine would otherwise play on)
    this.logoutDisposer = reaction(
      () => this.props.userStore.isLoggedIn,
      (loggedIn) => {
        if (!loggedIn && this.props.playerStore.playing) {
          appLog('PAUSED: signed out')
          this.pause()
        }
      }
    )
    this.replacedDisposer = reaction(
      () => this.props.userStore.replacedElsewhere,
      (replaced) => {
        if (replaced) {
          appLog('PAUSED: this account signed in on another device')
          this.pause()
          analytics.track({ event: 'replaced_by_other_device' })
        }
      }
    )

    this.playlistDisposer = reaction(
      () => ({
        id: this.props.userStore.id,
        isPremium: this.props.userStore.isPremium,
        isSubscribed: this.props.userStore.isSubscribed,
        isTrial: this.props.userStore.isTrial,
        disableMotivationQuotes: this.props.userStore.disableMotivationQuotes,
        explicitFilter: this.props.userStore.explicitFilter,
        adFrequency: this.props.playlistStore.adFrequency,
        businessMessages: this.props
          .audioMessageStore
          .audioMessages
          .map((audioMessage) => `${audioMessage.id}${audioMessage.enabled}`)
      }),
      () => {
        // Rebuild "Up next" straight away (e.g. after Light / Normal / Busy),
        // not only the next time the page happens to redraw. The playing
        // track carries on.
        this.shouldRefreshPlaylist = true
        clearTimeout(this.refreshTimer)
        this.refreshTimer = setTimeout(() => {
          if (this.shouldRefreshPlaylist) {
            this.shouldRefreshPlaylist = false
            this.refreshTracks()
          }
        }, 800)
      }
    )

    this.audioMessageDisposer = reaction(
      () => this.props.userStore.id,
      () => this.shouldRefreshAudioMessages = true
    )

    this.volumeDisposer = reaction(
      () => this.props.playerStore.normalizedVolume,
      (normalizedVolume) => {
        const { playerStore } = this.props
        const { volumeChangeInProgress, volume } = playerStore
        if (!volumeChangeInProgress) {
          this.setState({
            volume
          })
        }
        this.setAudioTagVolume(normalizedVolume)
      },
      { fireImmediately: true }
    )
  }

  componentDidUpdate (prevProps, prevState) {
    if (this.shouldRefreshAudioMessages) {
      const { audioMessageStore } = this.props
      audioMessageStore.loadAudioMessages()
      this.shouldRefreshAudioMessages = false
    }
    if (this.shouldRefreshPlaylist) {
      this.shouldRefreshPlaylist = false
      // Call refresh tracks in the next tick
      setTimeout(() => { this.refreshTracks() }, 500)
    }
  }

  componentWillUnmount () {
    clearTimeout(this.refreshTimer)
    clearInterval(this.allowanceTimer)
    clearInterval(this.progressTimer)
    clearInterval(this.activeLoginTimer)
    this.replacedDisposer && this.replacedDisposer()
    this.logoutDisposer && this.logoutDisposer()
    runInAction(() => {
      this.playlistDisposer()
      this.audioMessageDisposer()
      this.volumeDisposer()
    })
  }

  setAudioTagVolume (normalizedVolume: number) {
    if (this.audio && this.audio.audioEl) {
      this.audio.audioEl.volume = normalizedVolume
    }
  }

  async nextTrack (channel) {
    const {
      playlistStore
    } = this.props
    await playlistStore.nextTrack(channel.tags)
  }

  updateSongProgress () {
    const el = this.audio && this.audio.audioEl
    const position = el ? el.currentTime || 0 : 0
    const duration = el ? el.duration || 0 : 0
    if (Math.floor(position) !== Math.floor(this.state.songPosition) || duration !== this.state.songDuration) {
      this.setState({ songPosition: position, songDuration: duration })
    }
  }

  // Promotions toggled from the sidebar: Pro features, like the Setup pop-up.
  async togglePromotionalMessage (message, enabled) {
    try {
      await this.forceLogin()
      await this.forcePremium()
      this.onPromotionalMessagesStatusChanged(message, enabled)
    } catch (err) {}
  }

  async toggleMotivation (enabled) {
    try {
      await this.forceLogin()
      await this.forcePremium()
      this.onMotivationalMessagesStatusChanged(enabled)
    } catch (err) {}
  }

  // Free plan: FREE_MINUTES of music per hour for accounts without Pro.
  isFreePlan () {
    const { userStore } = this.props
    return userStore.isAuthenticated && !userStore.isPremium
  }

  lastAllowanceTick = 0
  tickFreeAllowance () {
    const { userStore, playerStore } = this.props
    const now = Date.now()
    const elapsed = this.lastAllowanceTick ? now - this.lastAllowanceTick : 0
    this.lastAllowanceTick = now

    if (!this.isFreePlan()) {
      if (this.state.freeRemainingMs !== null) {
        this.setState({ freeRemainingMs: null })
      }
      return
    }

    const userId = userStore.id
    const remaining = playerStore.playing
      ? freeAllowance.consume(userId, Math.min(elapsed, 5000), now)
      : freeAllowance.remainingMs(userId, now)

    if (remaining !== this.state.freeRemainingMs) {
      this.setState({ freeRemainingMs: remaining })
    }

    if (remaining <= 0 && playerStore.playing) {
      appLog('PAUSED: free plan listening limit reached')
      this.pause()
      this.showFreeSlotModal()
      analytics.track({
        event: 'free_slot_popup_showed'
      })
    }
  }

  async checkFreeSlot (channel) {
    const { userStore } = this.props
    if (this.isFreePlan() && freeAllowance.remainingMs(userStore.id) <= 0) {
      this.pause()
      this.showFreeSlotModal()
      throw new Error('No free listening left')
    }
  }

  async playPause () {
    try {
      const { playerStore, userStore } = this.props
      const { normalizedVolume, playing } = playerStore
      const { loadPlayer } = this.state
      if (!playing) {
        await this.forceLogin()
        if (userStore.replacedElsewhere) {
          return
        }
        if (this.isFreePlan() && freeAllowance.remainingMs(userStore.id) <= 0) {
          this.showFreeSlotModal()
          analytics.track({
            event: 'free_slot_popup_showed'
          })
          return
        }
      }

      if (!loadPlayer) {
        this.setState({
          loadPlayer: true
        })
        this.setAudioTagVolume(normalizedVolume)
      }

      appLog('play button:', playing ? 'pause' : 'play')
      if (playing) {
        this.pause()
      } else {
        this.lastAllowanceTick = Date.now()
        this.play()
      }
    } catch (err) {}
  }

  async pause (offline = false) {
    if (this.audio && this.audio.audioEl) {
      const currentTime = this.audio.audioEl.currentTime
      this.audio.audioEl.pause()
      if (!offline) {
        this.audio.audioEl.load()
      }
      this.audio.audioEl.currentTime = currentTime
      const { playlistStore, playerStore } = this.props
      playerStore.playing = false

      const { stationStore } = this.props
      const { selectedStation } = stationStore
      const { currentTrack } = playlistStore
      analytics.track({
        event: 'song_paused',
        songName: currentTrack.fullName,
        songUuid: currentTrack.uuid,
        selectedPlaylistId: selectedStation.id
      })
    }
  }

  async play () {
    if (this.audio && this.audio.audioEl) {
      this.audio.audioEl.play()
      const { playerStore } = this.props
      playerStore.playing = true
    }
  }

  async skip (channel) {
    try {
      await this.forceLogin()
      await this.forcePremium()

      const { playerStore, playlistStore } = this.props

      console.log("SKIPPED")
      appLog('skip button')

      const { currentTrack } = playlistStore
      if (hasNativeAudio() && this.audio && this.audio.skipNow) {
        // Phone app: move on, then tell the engine to jump to the new first track
        await playlistStore.nextTrack(channel.tags)
        this.audio.skipNow()
      } else {
        playerStore.skip(channel.tags)
      }

      analytics.track({
        event: 'song_skipped',
        songName: currentTrack.fullName || '',
        songUuid: currentTrack.uuid,
        selectedPlaylistId: channel.id
      })
    } catch (err) {}
  }

  async dislike (channel, song) {
    try {
      await this.forceLogin()
      await this.forcePremium()

      const { playlistStore } = this.props
      await playlistStore.dislikeSong(song.uuid)

      analytics.track({
        event: 'song_disliked',
        songName: song.fullName || '',
        songUuid: song.uuid,
        selectedPlaylistId: channel.id
      })

      this.showDislikeSongToast()
    } catch (err) {}
  }

  // Goes to Mixpanel as song_reported_explicit, with the song and the gym's account
  reportExplicit (channel, song) {
    if (!song || !song.uuid || this.state.reportedSongs[song.uuid]) return
    analytics.track({
      event: 'song_reported_explicit',
      songName: song.fullName || '',
      songUuid: song.uuid,
      selectedPlaylistId: channel.id
    })
    this.setState({ reportedSongs: { ...this.state.reportedSongs, [song.uuid]: true } })
    this.showReportedSongToast()
  }

  async forceLogin () {
    const { userStore } = this.props
    const { isAuthenticated } = userStore
    if (!isAuthenticated) {
      this.setState({
        showSignUpModal: true
      })
      analytics.track({ event: 'login_popup_showed' })
      throw new Error('Not registered')
    }
  }

  async forcePremium () {
    const { userStore } = this.props
    const { isPremium } = userStore
    if (!isPremium) {
      this.showPremiumModal()
      analytics.track({ event: 'pro_popup_showed' })
      throw new Error('Not premium')
    }
  }

  async showFreeSlotModal () {
    this.setState({
      showFreeSlotModal: true
    })
  }

  async showPremiumModal () {
    this.setState({
      showPremiumModal: true
    })
  }

  async showAudioMessageModal () {
    this.setState({
      showAudioMessageModal: true
    })
  }

  async showChangedPlaylistToast () {
    const { stationStore } = this.props
    const { selectedStation } = stationStore
    const Message = ({ closeToast }) => (
      <Alert color="info" >
        <p>The playlist will change once the current song ends.
        <br />
        If you wish to change the playlist immediately, use the <a style={{ color: "#ff0046" }}  onClick={ () => { this.skip(selectedStation) } }>skip button</a>.</p>
        <span onClick={ closeToast }>
          <Close />
        </span>
      </Alert>
    )

    toast.info(<Message />)
  }

  async showDislikeSongToast () {
    const Message = ({ closeToast }) => (
      <Alert color="info">
        <p>Ok, we will never play this song at your gym again.</p>
        <span onClick={ closeToast }>
          <Close />
        </span>
      </Alert>
    )

    toast.info(<Message />)
  }

  async showReportedSongToast () {
    const Message = ({ closeToast }) => (
      <Alert color="info">
        <p>Thanks, we will check this song.</p>
        <span onClick={ closeToast }>
          <Close />
        </span>
      </Alert>
    )

    toast.info(<Message />)
  }

  async showNoConnectionToast () {
    const { stationStore } = this.props
    const { selectedStation } = stationStore
    const Message = ({ closeToast }) => (
      <Alert color="info">
        <p>No internet connection.<br />Make sure Wi-Fi or cellular data is turned on, then <a className="alert-link" onClick={ () => { this.play(selectedStation) } }>try again.</a></p>
        <span onClick={ closeToast }>
          <Close />
        </span>
      </Alert>
    )

    toast.info(<Message />)
  }

  setVolume (volume) {
    const { playerStore } = this.props
    playerStore.volume = volume
  }

  loadPlaylistPromise
  async refreshTracks () {
    const { playlistStore, playerStore, stationStore } = this.props
    const { playing } = playerStore
    const { selectedStation } = stationStore

    try {
      if (this.loadPlaylistPromise) {
        this.loadPlaylistPromise.catch(() => {}) // Catch FLOW_CANCELED exception
        this.loadPlaylistPromise.cancel()
      }
      this.loadPlaylistPromise = playlistStore.loadPlaylist(selectedStation.tags, playing)
    } catch (err) {}
  }

  async removeTrack (channel, track) {
    try {
      await this.forceLogin()
      await this.forcePremium()
      const { playlistStore } = this.props
      playlistStore.removeTrack(track, channel.tags)

      analytics.track({
        event: 'song_removed',
        songName: track.fullName || '',
        songUuid: track.uuid,
        selectedPlaylistId: channel.id
      })
    } catch (err) {}
  }

  async onRequestQuotes () {
    try {
      await this.forceLogin()
      await this.forcePremium()
      /*const { email } = this.state
      const newWindow = window.open(`https://gymradio.typeform.com/to/lve1Gk?email=${email}`, '_blank')
      newWindow.focus()*/
      this.showAudioMessageModal()
      analytics.track({
        event: 'audio_promotions_popup_showed'
      })
    } catch (err) {}
  }

  async onDownloadedClick () {
    try {
      await this.forceLogin()
      await this.forcePremium()
    } catch (err) {
      this.showPremiumModal()
    }
  }

  async onExplicitFilterChange (option) {
    try {
      await this.forceLogin()
      await this.forcePremium()
      const { userStore } = this.props
      const value = option && option.value
      if (value) {
        userStore.setExplicitFilter(value)
        analytics.track({
          event: 'explicit_filter_changed',
          explicitFilterValue: value
        })
      }
    } catch (err) {}
  }

  async onRecentyAddedSelect (track) {
    try {
      await this.forceLogin()
      await this.forcePremium()
      const { playlistStore } = this.props
      playlistStore.addTrackByUuid(track.uuid, true)

      analytics.track({
        event: 'recenty_added_selected',
        audioMessageName: track.fullName,
        audioMessageUuid: track.uuid
      })
    } catch (err) {}
  }

  async setActiveStation (station) {
    this.props.stationStore.selectedStationId = station.id
    window.Intercom && window.Intercom('update', { selected_playlist_id: station.id })
  }

  async setActiveChannelRequest (channel) {
    const { playerStore } = this.props
    const { playing } = playerStore
    try {
      await this.forceLogin()
      await this.forcePremium()
      if (playing) {
        await this.showChangedPlaylistToast()
      }
      this.setActiveStation(channel)
      this.refreshTracks(channel.tags)

      analytics.track({
        event: 'playlist_selected',
        playlistId: channel.id
      })
    } catch (err) {}
  }

  // Audio callbacks
  async onEnded (channel) {
    try {
      await this.checkFreeSlot(channel)
      this.nextTrack(channel)
    } catch (err) {}
  }

  // Phone app: lets the music carry on with more songs from this station if the
  // app is swiped away (Pro accounts only; the free plan's limit needs the app).
  backgroundInfo (channel) {
    const { userStore, playlistStore, api } = this.props
    if (!channel || !userStore.isAuthenticated || !userStore.isPremium || !userStore.id) return null
    const tags = api.stringifyTags(playlistStore.getTagSelector(channel.tags || []))
    return {
      songsUrl: `${config.BASE_URL_API}/v1/song/list/random?tags=${tags}&type=song&provider=primary&limit=20&userId=${userStore.id}`,
      reportUrl: `${config.BASE_URL_EVENT_API}/event/gaia`,
      reportToken: userStore.gaiaToken || '',
      // songs played recently on this device, so the engine doesn't repeat them
      recent: playlistStore.recentSongIds(),
      artwork: NOW_PLAYING_ART
    }
  }

  // Phone app: the native engine played on by itself (e.g. while the phone was
  // locked). Move the web queue on by the same number of tracks.
  async onNativeAdvance (channel, count) {
    for (let i = 0; i < count; i++) {
      try {
        await this.checkFreeSlot(channel)
      } catch (err) {
        return
      }
      await this.nextTrack(channel)
    }
  }

  onError (status, text, channel) {
    const { playlistStore, stationStore } = this.props
    const { selectedStation } = stationStore
    const currentTrack = playlistStore.currentTrack
    analytics.track({
      event: 'playback_error',
      songName: currentTrack.fullName,
      songUuid: currentTrack.uuid,
      selectedPlaylistId: selectedStation.id,
      errorStatus: status,
      errorText: text
    })

    appLog('error', status, text, currentTrack && currentTrack.fullName)
    // Network error. In the phone app the engine retries by itself.
    if (status === 0 && hasNativeAudio()) {
      return
    }
    if (status === 0) {
      const { playerStore } = this.props
      const { playing } = playerStore
      if (playing) {
        this.pause(true)
        this.showNoConnectionToast()
      }
    // 3xx, 4xx, 5xx errors
    } else if (status >= 300) {
      this.nextTrack(channel)
    }
  }

  onPlay () {
    // console.log('play')
    /*this.setState({
      playing: true
    })*/
    const { playlistStore, stationStore, userStore, api } = this.props
    const { currentTrack } = playlistStore
    const { gaiaToken } = userStore
    const { selectedStation } = stationStore
    analytics.track({
      event: 'song_played',
      songName: currentTrack.fullName,
      songUuid: currentTrack.uuid,
      selectedPlaylistId: selectedStation.id
    })

    api.gaiaReport(gaiaToken, currentTrack.gaiaId)

    mediaSession.setNowPlaying({
      title: currentTrack.title,
      artist: displayArtist(currentTrack),
      station: selectedStation.name,
      image: selectedStation.image
    })
    mediaSession.setPlaybackState(true)
  }

  onPause () {
    mediaSession.setPlaybackState(false)
    // console.log('pause')
    /*this.setState({
      playing: false
    })*/
  }

  onMute () {
    this.setVolume(0)
  }

  onLoud () {

  }

  // Delete my account (App Store and Google Play require it in the app)
  showDeleteAccountModal () {
    this.setState({ showDeleteAccountModal: true, deleteAccountError: '', deleteAccountDone: false })
  }

  async confirmDeleteAccount () {
    const { userStore } = this.props
    this.setState({ deleteAccountBusy: true, deleteAccountError: '' })
    try {
      await userStore.deleteAccount()
      this.setState({ deleteAccountBusy: false, deleteAccountDone: true })
      setTimeout(() => userStore.logout(), 2500)
    } catch (e) {
      this.setState({ deleteAccountBusy: false, deleteAccountError: (e && e.message) || 'Something went wrong.' })
    }
  }

  // Modal
  onCancel () {
    this.setState({
      showSignUpModal: false,
      showPremiumModal: false,
      showFreeSlotModal: false,
      showAudioMessageModal: false,
      showDeleteAccountModal: false
    })
    if (window.location.search.includes('hs_chatbot=true')) {
      // Remove chatflow parameters from the URL
      const newUrl = window.location.href.replace(/(\?|&)hs_chatbot=true/, '');
      window.history.replaceState({}, document.title, newUrl);
      window.HubSpotConversations.widget.refresh({ openToNewThread: true });
      window.HubSpotConversations.widget.load({ widgetOpen: true });
    }
  }

  // Audio messages
  onMotivationalMessagesStatusChanged (enabled: boolean) {
    const { userStore } = this.props
    userStore.setEnableMotivationQuotes(enabled)
    analytics.track({
      event: 'motivational_messages_status_changed',
      enabled
    })
  }

  onPromotionalMessagesStatusChanged (audioMessage: any, enabled: boolean) {
    audioMessage.setEnabled(enabled)
    analytics.track({
      event: 'promotional_messages_status_changed',
      promotionalMessageTitle: audioMessage.title,
      promotionalMessageUuid: audioMessage.uuid,
      enabled
    })
  }

  /* redirects */
  goToRecurly () {
    analytics.track({ event: 'get_pro_button_clicked' })
    this.props.userStore.redirectToPricingPage()
  }

  render () {
    const {
      audioMessageStore,
      userStore,
      playerStore,
      playlistStore,
      stationStore
    } = this.props

    const {
      audioMessages
    } = audioMessageStore

    const {
      playing
    } = playerStore

    const {
      isPremium,
      isAuthenticated,
      gymName,
      email,
      explicitFilter, // Must stay here as dependency for mobx. Otherwise It will not reload playlist after explicit filter change
      isEmailVerified,
      playlists: customChannels
    } = userStore

    const {
      stations,
      selectedStation
    } = stationStore

    const {
      currentTrack
    } = playlistStore
    const { title, artist, url } = currentTrack

    const {
      volume,
      loadPlayer,
      showSignUpModal,
      showFreeSlotModal,
      showPremiumModal,
      showAudioMessageModal,
      freeRemainingMs,
      songPosition,
      songDuration
    } = this.state

    const { replacedElsewhere } = userStore

    const autoPlay = playing
    const shouldShowEmailVerificationNotification = isAuthenticated && !isEmailVerified

    return (
      <div>
        {
          shouldShowEmailVerificationNotification && (
            <EmailVerification
              email={ email }
            />
          )
        }
        <Header
          getPro={ () => {
            analytics.track({ event: 'get_pro_button_clicked' })
            this.showPremiumModal()
          } }
          showPricing={ () => {
            this.props.userStore.redirectToPricingPage()
          } }
          deleteAccount={ () => this.showDeleteAccountModal() }
          volume={ volume }
          onVolumeChange={ this.setVolume.bind(this) }
          onExplicitFilterChange={ this.onExplicitFilterChange.bind(this) }
          onMute={ this.onMute.bind(this) }
          onLoud={ this.onLoud }
        />
        <Zones
          isAuthenticated={ isAuthenticated }
          onZoneChannel={ (channelId) => {
            const station = stationStore.stations.find(s => s.id === channelId)
            if (station && stationStore.selectedStationId !== station.id) this.setActiveChannelRequest(station)
          } }
        />
        <SignUpModal
          isOpen={ showSignUpModal }
          onCancel={ this.onCancel.bind(this) }
        />
        <DeleteAccountModal
          isOpen={ this.state.showDeleteAccountModal }
          email={ userStore.email }
          hasSubscription={ userStore.isSubscribed && !userStore.isTrial && userStore.isPremium }
          busy={ this.state.deleteAccountBusy }
          error={ this.state.deleteAccountError }
          done={ this.state.deleteAccountDone }
          onConfirm={ this.confirmDeleteAccount.bind(this) }
          onCancel={ this.onCancel.bind(this) }
        />
        <FreeSlotModal
          isOpen={ showFreeSlotModal }
          freeMinutes={ freeAllowance.FREE_MINUTES }
          resetIn={ freeAllowance.formatMinutes(freeAllowance.msUntilReset(userStore.id)) }
          onSubmit={ this.goToRecurly.bind(this) }
          onCancel={ this.onCancel.bind(this) }
        />
        <ReplacedModal
          isOpen={ replacedElsewhere }
          onPlayHere={ () => userStore.login() }
        />
        <PremiumModal
          isOpen={ showPremiumModal }
          trialEnded={ isAuthenticated && !isPremium }
          onSubmit={ this.goToRecurly.bind(this) }
          onCancel={ this.onCancel.bind(this) }
        />
        <AudioMessageModal
          isOpen={ showAudioMessageModal }
          onMotivationalMessagesStatusChanged={ this.onMotivationalMessagesStatusChanged.bind(this) }
          onPromotionalMessagesStatusChanged={ this.onPromotionalMessagesStatusChanged.bind(this) }
          quotes={ audioMessages }
          onCancel={ this.onCancel.bind(this) }
        />
        <ToastContainer
          position="bottom-center"
          autoClose={ 8000 }
          hideProgressBar
          newestOnTop
          closeOnClick
          closeButton={ false }
          rtl={ false }
          pauseOnVisibilityChange
          pauseOnHover
        />
        <Container
          fluid={ true }
          className="app-panels"
        >
          <Row className="app-panels__row">
            <Col
              lg={{
                size: 4,
                offset: 0,
                order: "first"
              }}
              xs={{
                order: "last",
                size: 16,
                offset: 0
              }}
              className="stationbar d-none d-lg-block"
            >
              <Channels
                channels={ stations }
                playing={ playing }
                activeChannelId={ selectedStation.id }
                onSelect={ (channel) => { this.setActiveChannelRequest(channel) } }
              />
            </Col>
            <Col
              lg={{
                size: 7,
                offset: 0
              }}
              xs={{
                size: 16,
                offset: 0
              }}
              className="main"
            >
              <StationHero
                name={ selectedStation.name }
                image={ selectedStation.image }
                genres={ selectedStation.genres }
                title={ title }
                artist={ displayArtist(currentTrack) }
                isAdvert={ isAdvert(currentTrack) }
                playing={ playing }
                reported={ !!(currentTrack && this.state.reportedSongs[currentTrack.uuid]) }
                onReportExplicit={ currentTrack ? () => { this.reportExplicit(selectedStation, currentTrack) } : undefined }
              />
              <MobileAndTablet>
                <Channels
                  variant="strip"
                  channels={ stations }
                  playing={ playing }
                  activeChannelId={ selectedStation.id }
                  onSelect={ (channel) => { this.setActiveChannelRequest(channel) } }
                />
              </MobileAndTablet>
              <KeepScreenOn />
              {
                freeRemainingMs !== null && (
                  <div className="free-allowance">
                    <span>
                      Free plan: <strong>{ freeAllowance.formatClock(freeRemainingMs) }</strong> of music left this hour
                    </span>
                    { !isApp() && (
                      <button
                        type="button"
                        className="free-allowance__cta"
                        onClick={ this.goToRecurly.bind(this) }
                      >
                        Subscribe
                      </button>
                    ) }
                  </div>
                )
              }
              <Upcoming
                onRemoveTrack={ (track) => { this.removeTrack(selectedStation, track) } }
                onNextTrack={ () => { this.skip(selectedStation) }}
                onDownloadedClick={ () => { this.onDownloadedClick() }}
                onDislikeTrack={ (track) => {
                  this.dislike(selectedStation, track)
                  if (track.uuid === currentTrack.uuid) {
                    this.skip(selectedStation)
                  } else {
                    this.removeTrack(selectedStation, track)
                  }
                }}
              />
              { loadPlayer &&
                (
                  hasNativeAudio() ? (
                    <NativeAudio
                      ref={(ref) => {  this.audio = ref } }
                      playlistStore={ playlistStore }
                      artwork={ NOW_PLAYING_ART }
                      background={ this.backgroundInfo(selectedStation) }
                      autoPlay={ autoPlay }
                      onError={ (status, text) => { this.onError(status, text, selectedStation) } }
                      onAdvance={ (count) => this.onNativeAdvance(selectedStation, count) }
                      onPlay={ () => { this.props.playerStore.playing = true; this.onPlay(selectedStation) } }
                      onPause={ () => { this.props.playerStore.playing = false; this.onPause(selectedStation) } }
                    />
                  ) : (
                  <AudioElement
                    ref={(ref) => {  this.audio = ref } }
                    src={ url }
                    autoPlay={ autoPlay }
                    onError={ (status, text) => { this.onError(status, text, selectedStation) } }
                    onEnded={ () => { this.onEnded(selectedStation) } }
                    onPlay={ () => { this.onPlay(selectedStation) } }
                    onPause={ () => { this.onPause(selectedStation) } }
                  />
                  )
                )
              }
            </Col>
            <Col
              lg={{
                size: 5,
                offset: 0
              }}
              xs={{
                size: 16,
                offset: 0
              }}
              className="sidebar"
            >
              {
                // The Studio: Ads / Classes / Quotes as tabs (same on laptop, tablet and phone)
                userStore.isAuthenticated && (
                  <div className="widget">
                    <StudioPanel
                      key={ userStore.id }
                      onToggleMessage={ this.togglePromotionalMessage.bind(this) }
                      onToggleMotivation={ this.toggleMotivation.bind(this) }
                      onSubscribe={ () => this.forcePremium().catch(() => {}) }
                      onPreviewStart={ () => { if (this.props.playerStore.playing) this.pause() } }
                    />
                  </div>
                )
              }
            </Col>
          </Row>
        </Container>
        <NowPlayingBar
          title={ title }
          artist={ displayArtist(currentTrack) }
          stationName={ selectedStation.name }
          stationImage={ selectedStation.image }
          isAdvert={ isAdvert(currentTrack) }
          playing={ playing }
          position={ songPosition }
          duration={ songDuration }
          showVolume={ isAuthenticated }
          volume={ volume }
          onPlayPause={ () => { this.playPause(selectedStation) } }
          onSkip={ () => { this.skip(selectedStation) } }
          onDislike={ () => {
            this.dislike(selectedStation, currentTrack)
            this.skip(selectedStation)
          } }
          onVolumeChange={ this.setVolume.bind(this) }
          onMute={ this.onMute.bind(this) }
          onLoud={ this.onLoud }
        />
      </div>
    )
  }
}

export default inject((stores) => ({
  playlistStore: stores.playlistStore,
  stationStore: stores.stationStore,
  playerStore: stores.playerStore,
  userStore: stores.userStore,
  trackPreloaderStore: stores.trackPreloaderStore,
  recentSongStore: stores.recentSongStore,
  audioMessageStore: stores.audioMessageStore,
  api: stores.api
}))(
  observer(Player)
)

